import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api.js';
import { useApiMutation } from './useApi.js';

const SETTINGS_KEY = ['settings'];

// إعدادات النظام العامة (مشتركة بين الاستقبال/التسعير/الإعدادات).
export function useSettings() {
  return useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: async () => {
      const { data } = await api.get('/settings');
      return data || {};
    },
    // 60 ثانية: الاستقبال يقرأ رسم المرافق من هنا، ويجب أن يلتقط أي تعديل بسرعة.
    staleTime: 60 * 1000
  });
}

// قائمة أنواع التسعير المخفية (مصفوفة من: hourly|open|fixed|package).
export function useHiddenPricingTypes() {
  const { data } = useSettings();
  return Array.isArray(data?.hiddenPricingTypes) ? data.hiddenPricingTypes : [];
}

// قيمة تقريب إجمالي التسوية للأعلى (د.ع). 0 = إيقاف. الافتراضي 1000.
export function useRoundUpTo() {
  const { data } = useSettings();
  const v = Number(data?.roundUpTo);
  return Number.isFinite(v) && v >= 0 ? v : 1000;
}

// رسم دخول المرافق الواحد (بالغ بدون كارت). 0 = مجاناً.
export function useCompanionEntryFee() {
  const { data } = useSettings();
  return Math.max(0, Math.round(Number(data?.companionEntryFee) || 0));
}

/**
 * شعار الطباعة المركزي: يُرفع مرّة في الإعدادات ويُقرأ من هنا في كل قالب
 * طباعة، فلا يحتاج أي إيصال تعديلاً عند تغييره.
 *   url     مسار الصورة ('' = لا شعار مخصّص)
 *   enabled خيار «إظهار الشعار في الطباعة»
 *   visible الخلاصة: هل نرسم شعاراً مخصّصاً فعلاً
 * عند عدم وجود شعار أو تعطيل الخيار لا يُرسَم عنصر إطلاقاً — بلا مساحة فارغة.
 */
export function usePrintLogo() {
  const { data } = useSettings();
  const url = String(data?.printLogoUrl || '');
  const enabled = data?.printLogoEnabled !== false;
  return {
    url,
    enabled,
    visible: !!url && enabled,
    width: Number(data?.printLogoWidth) || null,
    height: Number(data?.printLogoHeight) || null
  };
}

/**
 * اسم الصالة المعتمد على الإيصالات والمطبوعات: «اسم الصالة على الإيصالات» من
 * الإعدادات إن حُدِّد، وإلا اسم الشركة المسجّل، وإلا اسم النظام.
 * مصدر واحد كي لا تختلف مطبوعة عن أخرى.
 */
export function useBusinessName(companyName) {
  const { data } = useSettings();
  const saved = String(data?.businessName || '').trim();
  return saved || String(companyName || '').trim() || 'Kids Zone';
}

export function useUploadPrintLogo() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (file) => {
      const form = new FormData();
      form.append('logo', file);
      return api.post('/settings/print-logo', form).then((r) => r.data);
    },
    successMessage: 'تم حفظ شعار الطباعة',
    onSuccess: (data) => {
      if (data) qc.setQueryData(SETTINGS_KEY, data);
      qc.invalidateQueries({ queryKey: SETTINGS_KEY });
    }
  });
}

export function useDeletePrintLogo() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: () => api.delete('/settings/print-logo').then((r) => r.data),
    successMessage: 'تم حذف شعار الطباعة',
    onSuccess: (data) => {
      if (data) qc.setQueryData(SETTINGS_KEY, data);
      qc.invalidateQueries({ queryKey: SETTINGS_KEY });
    }
  });
}

// `successMessage: null` يُسكت رسالة النجاح التلقائية لمن يريد التحقّق من
// القيمة المُعادة من الخادم قبل إعلان النجاح للمستخدم.
export function useUpdateSettings({ successMessage = 'تم حفظ الإعدادات' } = {}) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (body) => api.put('/settings', body).then((r) => r.data),
    successMessage,
    onSuccess: (data) => {
      if (data) qc.setQueryData(SETTINGS_KEY, data);
      qc.invalidateQueries({ queryKey: SETTINGS_KEY });
      // «إلزام الشفت» يُقرأ مع الشفت الحالي — نحدّثه حتى تتغيّر البوابة فوراً
      qc.invalidateQueries({ queryKey: ['shift'] });
    }
  });
}
